import { useCallback, useState } from 'react';
import {
  Button,
  Dialog,
  FileInfo,
  FileUpload,
  Flex,
  TextInput,
  Textarea,
  createForm,
  useId,
  useToasts,
  validateFiles,
} from '@applyboard/crystal-ui';
import { useEventTracker } from '@applyboard/ui-utils';
import { getBase64 } from '../../common/utils/getBase64';
import { UserFeedBackType, useUserSubmitFeedBack } from '../../data/submitFeedBack';
import useFileValidation from '../../pages/LoansForm/workflow/educationSummary/englishTestScores/fileUploadValidate';
import { SUPPORTED_FILES_MIME_TO_EXTENSION_MAP } from '../../pages/RbcGicFormNew/constants';

interface InProductFeedBackFormProps {
  feedbackText: string;
  email: string;
  screenShotData: FileInfo[];
}

type InProductFeedBackProps = {
  feedbackText?: string;
  email?: string;
  screenShotData?: FileInfo[];
};

const serializeFile = async (inputFile: File): Promise<{ file_name: string; content: string }> => {
  const base64 = (await getBase64(inputFile)) as string;
  const fileExtension = SUPPORTED_FILES_MIME_TO_EXTENSION_MAP.get(inputFile.type);
  if (!fileExtension) {
    throw new Error('Unsupported file type');
  }
  const fileName = inputFile.name;
  return { file_name: fileName, content: base64 };
};

const { Field, Form } = createForm<InProductFeedBackFormProps>();

export function InProductFeedBackDialog(opt?: InProductFeedBackProps) {
  const defaultFormValues: InProductFeedBackFormProps = {
    feedbackText: opt?.feedbackText || '',
    email: opt?.email || '',
    screenShotData: opt?.screenShotData || [],
  };
  const formId = useId();
  const [open, setOpen] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const { trackEvent } = useEventTracker();
  const toasts = useToasts();
  const { userFeedBack } = useUserSubmitFeedBack();
  const { fileValidateUpload } = useFileValidation();

  const submitCallback = useCallback(
    async (values: InProductFeedBackFormProps) => {
      trackEvent({
        event: 'VAS - Vas Hub Page - User Feedback submitted',
      });
      setSubmitDisabled(true);
      let screenShotData: { file_name: string; content: string }[] = [];
      if (values.screenShotData && values.screenShotData.length > 0) {
        screenShotData = await Promise.all(
          values.screenShotData?.map(async fileInfo => {
            return await serializeFile(fileInfo.file);
          }),
        );
      }
      const userSubmitFeedBackPayload: UserFeedBackType = {
        email: values.email || undefined,
        feedback: values.feedbackText,
        screenshots: screenShotData,
      };
      userFeedBack(
        userSubmitFeedBackPayload,
        () => {
          setSubmitDisabled(false);
          toasts.positive(`Feedback submitted successfully`);
          setOpen(false);
        },
        error => {
          console.error('Failed to submit user feedback', error);
          toasts.negative(new Error('Failed to submit user feedback'));
          setSubmitDisabled(false);
        },
      );
    },
    [trackEvent, userFeedBack, toasts],
  );

  return (
    <Dialog open={open} onOpenChange={(state: boolean) => setOpen(state)} size="md">
      <Dialog.Trigger>
        <Button
          aria-label="messages"
          size="md"
          intent="primary"
          emphasis="contained"
          width="hug"
          disabled={submitDisabled}
        >
          Leave feedback
        </Button>
      </Dialog.Trigger>
      <Dialog.Heading>User Feedback</Dialog.Heading>
      <Dialog.Content>
        <Form id={formId} defaultValues={defaultFormValues} onSubmit={submitCallback}>
          <Flex align="stretch" direction="column" gap={{ lg: 8, sm: 4, xs: 2 }}>
            <Field as={TextInput} name="email" label="Email" required={false} />
            <Field
              as={Textarea}
              name="feedbackText"
              label="Feedback Detail"
              required="Feedback detail is required"
            />
            <Field
              as={FileUpload}
              size="sm"
              allowedFileTypes={['.jpeg', '.png', '.pdf', 'jpg']}
              helpText="Acceptable file formats are PDF, PNG, JPG or JPEG. File size limit is 1 MB, and a maximum of 5 files can be uploaded."
              name="screenShotData"
              maxFileSize={1000000} // 1 MB
              label="Upload Screenshot"
              multiple={true}
              required={false}
              validate={value => validateFiles(value, 5)}
              uploadFile={fileValidateUpload}
            >
              <FileUpload.Button>Upload</FileUpload.Button>
            </Field>
          </Flex>
        </Form>
      </Dialog.Content>
      <Dialog.Actions>
        <Dialog.CloseButton width="fill">Cancel</Dialog.CloseButton>
        <Button intent="primary" width="fill" type="submit" form={formId} disabled={submitDisabled}>
          Submit
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
}
